/**
 * MDARemittancePage — `/government/remittance`.
 *
 * Live remittance dashboard backed by the new endpoints:
 *   GET  /v1/government/remittance/summary
 *   GET  /v1/government/remittance/runs
 *   POST /v1/government/remittance/runs
 *   POST /v1/government/remittance/runs/:id/mark-remitted
 */
import { type JSX, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createRemittanceRun,
  getRemittanceSummary,
  listRemittanceRuns,
  markRemittanceRunRemitted,
} from '../../lib/api/government';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const AGENCIES = ['NIMC', 'NPC', 'FRSC', 'FIRS', 'INEC'];

function fmtNgn(amount: string | undefined): string {
  if (!amount) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return `NGN ${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function isoStartOfMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0).toISOString();
}

function isoNow(): string {
  return new Date().toISOString();
}

export function MDARemittancePage(): JSX.Element {
  const qc = useQueryClient();
  const summary = useQuery({
    queryKey: ['gov', 'remittance', 'summary'],
    queryFn: getRemittanceSummary,
    retry: false,
  });
  const runs = useQuery({
    queryKey: ['gov', 'remittance', 'runs'],
    queryFn: () => listRemittanceRuns({ pageSize: 50 }),
    retry: false,
  });

  const [agency, setAgency] = useState('NIMC');
  const [periodStart, setPeriodStart] = useState(isoStartOfMonth());
  const [periodEnd, setPeriodEnd] = useState(isoNow());
  const [bankRefDraft, setBankRefDraft] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createRemittanceRun,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'remittance'] });
      setFormErr(null);
    },
    onError: (err) => setFormErr((err as Error).message),
  });

  const markRemitted = useMutation({
    mutationFn: ({ id, bankRef }: { id: string; bankRef: string }) =>
      markRemittanceRunRemitted(id, { bankRef }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'remittance'] });
    },
  });

  const summaryDenied =
    summary.isError && summary.error instanceof ApiError && (summary.error.status === 401 || summary.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader title="MDA remittance" description="Agency collections + remittance pipeline." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total collected"
          value={summary.isPending ? '—' : fmtNgn(summary.data?.totalCollected)}
          hint="Sum of agency fees across all runs"
          icon={DollarSign}
        />
        <KPICard
          title="Remitted"
          value={summary.isPending ? '—' : fmtNgn(summary.data?.remitted)}
          hint="Disbursed to MDAs"
          icon={CheckCircle}
          tone="success"
        />
        <KPICard
          title="Pending"
          value={summary.isPending ? '—' : fmtNgn(summary.data?.pending)}
          hint={`${summary.data?.pendingRuns ?? 0} run(s) awaiting`}
          icon={Clock}
        />
        <KPICard
          title="Overdue"
          value={summary.isPending ? '—' : fmtNgn(summary.data?.overdue)}
          hint={`${summary.data?.overdueRuns ?? 0} run(s) past SLA`}
          icon={AlertTriangle}
          tone={(summary.data?.overdueRuns ?? 0) > 0 ? 'warning' : undefined}
        />
      </div>

      {summaryDenied ? (
        <DeferredNotice title="Permission required">
          The token doesn&apos;t carry <code className="font-mono">Government/readRemittance</code>.
        </DeferredNotice>
      ) : null}

      <section className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Run remittance for a period</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Agency</label>
            <select
              data-testid="run-agency"
              value={agency}
              onChange={(e) => setAgency(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              {AGENCIES.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Period start</label>
            <input
              data-testid="run-period-start"
              type="datetime-local"
              value={periodStart.slice(0, 16)}
              onChange={(e) => setPeriodStart(new Date(e.target.value).toISOString())}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Period end</label>
            <input
              data-testid="run-period-end"
              type="datetime-local"
              value={periodEnd.slice(0, 16)}
              onChange={(e) => setPeriodEnd(new Date(e.target.value).toISOString())}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
        </div>
        <button
          type="button"
          data-testid="create-run"
          onClick={() => create.mutate({ agency, periodStart, periodEnd })}
          disabled={create.isPending}
          className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 hover:bg-nipost-green-dark"
        >
          {create.isPending ? 'Computing…' : 'Compute & persist run'}
        </button>
        {formErr ? <p className="text-xs text-danger">{formErr}</p> : null}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Runs</h2>
        {runs.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : runs.isError ? (
          <DeferredNotice title="Service unreachable">{(runs.error as Error).message}</DeferredNotice>
        ) : (runs.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No remittance runs yet. Use the form above to compute one.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Agency</th>
                  <th className="text-left px-4 py-2">Period</th>
                  <th className="text-right px-4 py-2">Requests</th>
                  <th className="text-right px-4 py-2">Collected</th>
                  <th className="text-right px-4 py-2">NIPOST</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Bank ref</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {runs.data?.items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs text-gray-900">{r.agency}</td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(r.periodStart).toLocaleDateString()} – {new Date(r.periodEnd).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{r.requestCount}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{fmtNgn(r.collectedAmount)}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{fmtNgn(r.nipostCharge)}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-xs">
                      {r.status === 'REMITTED' ? (
                        <span className="font-mono text-gray-900">{r.bankRef}</span>
                      ) : (
                        <div className="flex gap-1">
                          <input
                            data-testid={`bank-ref-${r.id}`}
                            type="text"
                            placeholder="Bank ref…"
                            value={bankRefDraft[r.id] ?? ''}
                            onChange={(e) =>
                              setBankRefDraft((s) => ({ ...s, [r.id]: e.target.value }))
                            }
                            className="px-2 py-1 border border-border rounded text-[11px] font-mono"
                          />
                          <button
                            type="button"
                            data-testid={`mark-remitted-${r.id}`}
                            disabled={!bankRefDraft[r.id]}
                            onClick={() => {
                              const bankRef = bankRefDraft[r.id];
                              if (bankRef) markRemitted.mutate({ id: r.id, bankRef });
                            }}
                            className="px-2 py-1 bg-nipost-green text-white rounded text-[11px] font-medium disabled:opacity-50 hover:bg-nipost-green-dark"
                          >
                            Mark remitted
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
